/** Envelope of a scripted look to the side: swing in, hold, swing back (seconds, degrees). */
export interface GlanceShape {
  readonly yawDegrees: number;
  readonly attack: number;
  readonly hold: number;
  readonly release: number;
}

/** A glance that also orbits closer and lower and lifts the look-at point (ShowcaseModifier). */
export interface ShowcaseShape extends GlanceShape {
  /** Meters the camera moves in towards the runner. */
  readonly closerBy: number;
  /** Meters the camera drops (negative = lower). */
  readonly heightOffset: number;
  /** Meters the look-at point rises (from the feet towards the face). */
  readonly targetLift: number;
}

/**
 * Third-person camera rig. Damping values are exponential rates (1/s):
 * higher = snappier. They are frame-rate independent (see utils/math.ts → damp).
 */
export interface CameraConfig {
  /** Distance behind the player along the track (meters). */
  readonly followDistance: number;
  /** Camera height above the player's feet (meters). */
  readonly height: number;
  /** Point the camera looks at: ahead of / above the player's feet. */
  readonly lookAhead: number;
  readonly lookHeight: number;
  /** 0 = camera stays centered, 1 = camera fully follows the player's lane offset. */
  readonly lateralFollowFactor: number;
  /** 0 = camera ignores jumps, 1 = camera follows height fully. */
  readonly verticalFollowFactor: number;
  /**
   * Portrait phones (aspect below `maxAspect`): the minimum horizontal FOV widens the vertical one a lot, the runner
   * looked small and far away (user 2026-10-03) – a closer, lower camera and a slightly narrower minimum.
   */
  readonly portrait: {
    readonly maxAspect: number;
    readonly followDistance: number;
    readonly height: number;
    readonly minHorizontalDegrees: number;
    /**
     * Lateral damping in portrait (instead of positionDamping.lateral): close behind the runner the whole picture moves
     * with a lane change – a slow follow made it look late and jerky on phones (user 2026-10-03).
     */
    readonly lateralDamping: number;
  };

  readonly positionDamping: {
    readonly lateral: number;
    readonly vertical: number;
    readonly forward: number;
  };
  /** Damping of the camera heading when the track direction changes (turns). */
  readonly rotationDamping: number;

  readonly fov: {
    readonly baseDegrees: number;
    readonly maxDegrees: number;
    readonly damping: number;
    /**
     * Lower bound for the horizontal FOV. Babylon keeps the vertical FOV fixed,
     * so portrait phones would otherwise see less than the 3 lanes.
     */
    readonly minHorizontalDegrees: number;
  };
  readonly nearPlane: number;
  readonly farPlane: number;

  readonly shake: {
    readonly maxOffset: number;
    readonly maxRollDegrees: number;
    readonly frequency: number;
    /** Trauma decay per second (trauma is clamped to 0..1). */
    readonly decay: number;
  };
  readonly landing: {
    /** Downwards dip in meters per m/s of impact speed. */
    readonly dipPerImpactSpeed: number;
    readonly maxDip: number;
    readonly stiffness: number;
    readonly damping: number;
  };
  /**
   * While sliding the camera drops and looks slightly up, so the player can
   * see under overhead obstacles (scaffold passages) and what comes after.
   */
  readonly slide: {
    /** Height change while sliding (meters, negative = lower). */
    readonly heightOffset: number;
    /** Look target is raised by this much → the camera looks up/ahead. */
    readonly lookUpOffset: number;
    /** Camera moves this much closer to the player (meters, negative = further back). */
    readonly closerBy: number;
    /** Duration of the smooth camera move into / out of the slide view (s) – spring, no jumps. */
    readonly blendInTime: number;
    readonly blendOutTime: number;
  };
  /** Minimum gap between camera and the underside of overhead obstacles (meters). */
  readonly overheadClearance: number;
  /** The camera looks this far ahead for overhead obstacles and glides down in time (meters). */
  readonly overheadLookAhead: number;
  /** Duration of the glide down under overhead obstacles (s) – quick, so the camera never touches them. */
  readonly overheadSmoothTime: number;
  /** Duration of the glide back up after passing an overhead obstacle (s) – calm. */
  readonly overheadReleaseTime: number;
  /** Colliders whose bottom is above this height count as "overhead" for the camera. */
  readonly overheadMinHeight: number;
  /**
   * The camera stays at least this high above the walkable ground along its path (meters) – the low
   * slide view on a container roof would otherwise dip into the container. Rises in time like the
   * overhead glide (overheadLookAhead / overheadSmoothTime / overheadReleaseTime).
   */
  readonly groundClearance: number;
  /**
   * Walkable tops (pipe crowns, container roofs) are ground for the camera where the runner ran on
   * them, ceilings where it ran underneath (RunnerHeightTrail): the runner's height is remembered over
   * `length` m behind it (longer than followDistance + overheadLookAhead), in `spacing` m slices.
   */
  readonly runnerTrail: { readonly length: number; readonly spacing: number };
  readonly turnAnticipation: {
    /** Camera yaws slightly towards the decided/forced turn before the junction. */
    readonly enabled: boolean;
    readonly maxYawDegrees: number;
    readonly startDistance: number;
  };
  /**
   * Junction turns: the track frame jumps to the exit leg there (every lane turns at its own
   * corner), the runner does not. The rig keeps its pivot where it was and lets the step fade out.
   */
  readonly turnFollow: {
    /** A track heading change above this between two frames is such a jump (curves: well under 1° per frame). */
    readonly jumpDegrees: number;
    /** Fade-out of the kept step (1/s). */
    readonly recoveryDamping: number;
  };
  /**
   * Extra-life drink ("Prost!"): the world waits, the camera swings round to the runner's front side, so the
   * drink is seen, and back while the bottle goes away. attack + hold + release ≈ the drink (DrinkBreak).
   */
  readonly drinkShowcase: ShowcaseShape;
  /**
   * Riding the dozer (SEG_DOZER_60M): the rider sits 1.48 m up on a 4.2 m machine – the camera rises and moves
   * back so machine, blade and the lanes ahead stay in view, and looks a little higher (at the rider, not the road).
   */
  readonly drive: {
    /** Height change (m, positive = higher). */
    readonly heightOffset: number;
    /** Meters closer to the rider (negative = further back). */
    readonly closerBy: number;
    /** The look-at point rises by this much (m). */
    readonly lookUpOffset: number;
    /** Spring blend into / out of the ride view (s). */
    readonly blendInTime: number;
    readonly blendOutTime: number;
    /**
     * ructec-Bulli instead of the values above (user 2026-10-05: "das Spiel muss kurz switchen zwischen Fahrspiel und
     * Runspiel"): higher and further back over the van and its roof rack, and the picture widens with the speed.
     */
    readonly bus: {
      readonly heightOffset: number;
      readonly closerBy: number;
      readonly lookUpOffset: number;
      /** Extra field of view (degrees) at the van's top speed (speed share 1). */
      readonly fovAtSpeed: number;
    };
  };
}

export const CAMERA_CONFIG: CameraConfig = {
  // Overview from above: ~18° down onto the track (was 2.7 m / 6 m / 1.1 m: ~8°, too flat).
  // height stays below the lowest enclosure roof (interior 3.8 m − overheadClearance).
  followDistance: 5,
  height: 3.5,
  lookAhead: 5,
  lookHeight: 0.3,
  lateralFollowFactor: 0.75,
  verticalFollowFactor: 0.45,
  // same ~17° down onto the track, ≈ 1.4 × the runner's size on a portrait phone
  portrait: { maxAspect: 1, followDistance: 3.8, height: 2.9, minHorizontalDegrees: 46, lateralDamping: 14 },

  positionDamping: {
    lateral: 7,
    vertical: 5,
    forward: 30,
  },
  rotationDamping: 6,

  fov: {
    baseDegrees: 60,
    maxDegrees: 70,
    damping: 2,
    minHorizontalDegrees: 50,
  },
  // 0.3, not 0.1 (2026-10-04): three times the depth precision – at 0.1 the 2 cm between the gantry banner and its
  // backing (and glass in frames, nets on scaffolds) flickered from ~150 m on. The camera never gets closer than ~1 m.
  nearPlane: 0.3,
  farPlane: 400,

  shake: {
    maxOffset: 0.25,
    maxRollDegrees: 2.5,
    frequency: 22,
    decay: 1.8,
  },
  landing: {
    dipPerImpactSpeed: 0.02,
    maxDip: 0.25,
    stiffness: 160,
    damping: 18,
  },
  // The slide view is unchanged in absolute terms (camera 1.3 m high, 5.2 m back, looking at 1.5 m
  // 5 m ahead) – the offsets bridge from the higher run view; the blend eases between both.
  slide: {
    heightOffset: -2.2,
    lookUpOffset: 1.2,
    closerBy: -0.2,
    blendInTime: 0.28,
    // back up to the run view quickly after the slide (user 2026-10-04: "nach dem Rutschen schnell wieder schwenken" –
    // was 0.5, the camera hung low for over a second)
    blendOutTime: 0.18,
  },
  overheadClearance: 0.2,
  overheadLookAhead: 4,
  overheadSmoothTime: 0.1,
  overheadReleaseTime: 0.4,
  overheadMinHeight: 0.6,
  // below the 1.3 m slide view on the road – only raised ground (roofs, platforms) ever lifts the camera
  groundClearance: 0.8,
  runnerTrail: { length: 16, spacing: 0.25 },
  turnAnticipation: {
    enabled: true,
    maxYawDegrees: 12,
    startDistance: 12,
  },
  turnFollow: {
    jumpDegrees: 45,
    recoveryDamping: 5,
  },
  drinkShowcase: { yawDegrees: 110, attack: 0.5, hold: 1.97, release: 0.5, closerBy: 2.8, heightOffset: -2.1, targetLift: 1.35 },
  // 4.4 m high, 6.4 m behind the rider: 2.3 m above the machine's top, the blade and two rows ahead in the picture
  drive: {
    heightOffset: 0.9,
    closerBy: -1.4,
    lookUpOffset: 0.7,
    blendInTime: 0.45,
    blendOutTime: 0.6,
    // the van is 2 m high with its rack: the camera rides 1.9 m higher and 3.2 m further back, looking over it at the road
    // further back and higher (user 2026-10-05: "Kamera im Bulli weiter hinten, um alles gut zu sehen"): the rival beside
    // and behind the van and the oncoming traffic ahead stay in the picture
    bus: { heightOffset: 2.8, closerBy: -6.5, lookUpOffset: 1.1, fovAtSpeed: 12 },
  },
};
